import React, { useMemo, useState, useEffect } from "react";
import { Grid } from "@aws-amplify/ui-react";
import {
  CCard,
  CCardBody,
  CCardHeader,
  CRow,
  CCol,
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
} from "@coreui/react";
import { Bar, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarElement,
  ArcElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Title,
} from "chart.js";
import ConfirmModal from "components/utils/ConfirmModal";

ChartJS.register(
  BarElement,
  ArcElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Title
);

const sampleData = {
  farms: [
    { id: "farm-1", name: "Finca La Esperanza", active: true },
    { id: "farm-2", name: "Finca El Roble", active: true },
    { id: "farm-3", name: "Finca Inactiva", active: false },
  ],
  lots: [
    {
      id: "lot-1",
      farmId: "farm-1",
      name: "Lote 1",
      area: 1500,
      perimeter: 160,
      active: true,
    },
    {
      id: "lot-2",
      farmId: "farm-1",
      name: "Lote 2",
      area: 800,
      perimeter: 110,
      active: true,
    },
    {
      id: "lot-3",
      farmId: "farm-2",
      name: "Lote A",
      area: 1200,
      perimeter: 140,
      active: true,
    },
  ],
  units: [
    { id: "u1", lotId: "lot-1", unitType: "Tree", state: "Semilla" },
    {
      id: "u2",
      lotId: "lot-1",
      unitType: "Tree",
      state: "Crecimiento",
    },
    {
      id: "u3",
      lotId: "lot-1",
      unitType: "Microlot",
      state: "Producción",
    },
    { id: "u4", lotId: "lot-1", unitType: "Tree", state: "Semilla" },
    { id: "u5", lotId: "lot-2", unitType: "Tree", state: "Injerto" },
    { id: "u6", lotId: "lot-3", unitType: "Microlot", state: "Soca" },
    { id: "u7", lotId: "lot-1", unitType: "Tree", state: "Producción" },
    { id: "u8", lotId: "lot-1", unitType: "Tree", state: "Reinjertar" },
    { id: "u9", lotId: "lot-1", unitType: "Tree", state: "Otro" },
  ],
};

export default function ReportTreeCounts(props) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const data = props.data || sampleData || { farms: [], lots: [], units: [] };

  // farms activos
  const farms = useMemo(
    () => (data.farms || []).filter((f) => f.active),
    [data]
  );
  const [selectedFarmId, setSelectedFarmId] = useState(farms[0]?.id || "");

  // lots dependiendo de la finca seleccionada
  const lotsByFarm = useMemo(
    () =>
      (data.lots || []).filter((l) => l.farmId === selectedFarmId && l.active),
    [data, selectedFarmId]
  );
  const [selectedLotId, setSelectedLotId] = useState(lotsByFarm[0]?.id || "");

  // sincronizar lote inicial cuando cambian available lots
  useEffect(() => {
    setSelectedLotId(lotsByFarm[0]?.id || "");
  }, [lotsByFarm]);

  // lote seleccionado completo
  const selectedLot = useMemo(
    () => (data.lots || []).find((l) => l.id === selectedLotId) || null,
    [data, selectedLotId]
  );

  // conteo por estado para el lote seleccionado
  const countsByState = useMemo(() => {
    if (!selectedLot) return {};
    const items = (data.units || []).filter((u) => u.lotId === selectedLot.id);
    return items.reduce((acc, u) => {
      const state = u.state || "Otro";
      acc[state] = (acc[state] || 0) + 1;
      return acc;
    }, {});
  }, [data, selectedLot]);

  const totalUnits = Object.values(countsByState).reduce((a, b) => a + b, 0);

  // labels/values
  const stateLabels = useMemo(
    () => Object.keys(countsByState),
    [countsByState]
  );
  const stateValues = useMemo(
    () => stateLabels.map((k) => countsByState[k]),
    [stateLabels, countsByState]
  );

  // paleta pastel suave
  const PASTEL_COLORS = [
    "#90d47dff", // green pastel
    "#f09bb4ff", // pink pastel
    "#fcdc86ff", // yellow pastel
    "#8eb4ffff", // blue pastel
    "#ce99f7ff", // purple pastel
    "#ffbe90ff", // orange pastel
    "#98eccdff", // mint pastel
  ];

  const colorsForData = useMemo(
    () => stateLabels.map((_, i) => PASTEL_COLORS[i % PASTEL_COLORS.length]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stateLabels]
  );

  const chartHeightPx = props.chartHeightPx || 360; // altura fija, por defecto 360px (puedes sobreescribir vía props)

  const barData = useMemo(
    () => ({
      labels: stateLabels,
      datasets: [
        {
          label: "Unidades",
          data: stateValues,
          backgroundColor: colorsForData,
          borderColor: colorsForData,
          borderWidth: 1,
        },
      ],
    }),
    [stateLabels, stateValues, colorsForData]
  );

  const pieData = useMemo(
    () => ({
      labels: stateLabels,
      datasets: [
        {
          data: stateValues,
          backgroundColor: colorsForData,
          hoverOffset: 8,
        },
      ],
    }),
    [stateLabels, stateValues, colorsForData]
  );

  const commonOptions = {
    responsive: true,
    maintainAspectRatio: false, // permite controlar height desde el contenedor
    plugins: {
      legend: { position: "bottom" },
      tooltip: { enabled: true },
    },
  };

  const barOptions = {
    ...commonOptions,
    plugins: {
      ...commonOptions.plugins,
      legend: { position: "top" },
      title: { display: false },
    },
    scales: {
      y: { beginAtZero: true, ticks: { precision: 0 } },
    },
  };

  const pieOptions = {
    ...commonOptions,
    plugins: {
      ...commonOptions.plugins,
      legend: { position: "bottom" },
    },
  };

  // modal confirm delete placeholder
  const [modalDetails, setModalDetails] = useState({
    visible: false,
    itemId: null,
    itemName: null,
  });
  function handleConfirmDelete() {
    setModalDetails({ visible: false, itemId: null });
  }

  return (
    <div>
      <span className="fs-3">Conteo de Árboles</span>

      <div className="p-3">
        {/* filtros mínimos: finca + lote usando select nativo */}
        <Grid
          className="mb-3"
          templateColumns={{ medium: "1fr 1fr" }}
          rowGap="15px"
          columnGap="15px"
        >
          <div>
            <label className="form-label">Finca</label>
            <select
              className="form-select"
              value={selectedFarmId}
              onChange={(e) => setSelectedFarmId(e.target.value)}
            >
              {(farms || []).map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="form-label">Lote</label>
            <select
              className="form-select"
              value={selectedLotId}
              onChange={(e) => setSelectedLotId(e.target.value)}
            >
              {(lotsByFarm || []).map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>
          </div>
        </Grid>

        {/* tarjetas resumen */}
        <CRow className="mb-3">
          <CCol md={4} sm={12} className="mb-2">
            <CCard>
              <CCardHeader className="small text-muted">Área (m²)</CCardHeader>
              <CCardBody>
                <div className="fs-4">{selectedLot?.area ?? "—"}</div>
              </CCardBody>
            </CCard>
          </CCol>

          <CCol md={4} sm={12} className="mb-2">
            <CCard>
              <CCardHeader className="small text-muted">
                Perímetro (m)
              </CCardHeader>
              <CCardBody>
                <div className="fs-4">{selectedLot?.perimeter ?? "—"}</div>
              </CCardBody>
            </CCard>
          </CCol>

          <CCol md={4} sm={12} className="mb-2">
            <CCard>
              <CCardHeader className="small text-muted">
                Total Unidades
              </CCardHeader>
              <CCardBody>
                <div className="fs-4">{totalUnits}</div>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>

        {/* gráficos: barras con color pastel y torta */}
        <CRow>
          <CCol md={6} sm={12} className="mb-3">
            <CCard>
              <CCardHeader className="small text-muted">
                Distribución por Estado
              </CCardHeader>
              <CCardBody style={{ height: chartHeightPx, width: "100%" }}>
                {totalUnits === 0 ? (
                  <div className="text-muted">
                    No hay unidades para este lote
                  </div>
                ) : (
                  <Bar data={barData} options={barOptions} />
                )}
              </CCardBody>
            </CCard>
          </CCol>

          <CCol md={6} sm={12} className="mb-3">
            <CCard>
              <CCardHeader className="small text-muted">
                Distribución porcentual
              </CCardHeader>
              <CCardBody style={{ height: chartHeightPx, width: "100%" }}>
                {totalUnits === 0 ? (
                  <div className="text-muted">
                    No hay unidades para este lote
                  </div>
                ) : (
                  <Pie data={pieData} options={pieOptions} />
                )}
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      </div>

      <ConfirmModal
        visible={modalDetails.visible}
        body={`¿Estás seguro de eliminar el registro ${modalDetails.itemName}?`}
        onCancel={() => setModalDetails({ visible: false, itemId: null })}
        onConfirm={handleConfirmDelete}
      />

      {/* Modal genérico si se necesita (placeholder) */}
      <CModal
        size="xl"
        alignment="center"
        visible={false}
        onClose={() => {}}
        backdrop="static"
      >
        <CModalHeader>
          <CModalTitle>Modal</CModalTitle>
        </CModalHeader>
        <CModalBody style={{ overflow: "hidden" }}>
          {/* contenido si se requiere */}
        </CModalBody>
      </CModal>
    </div>
  );
}
